import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { Globe, MapPin, Navigation, Search, Sparkles, X } from 'lucide-react';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { isAiConciergeEnabled, isAiConciergeUnavailable } from '../../util/aiConcierge';
import { CITY_LANDING_SLUGS } from '../../util/cityLandingPageConfig';
import { encodeLatLngBounds } from '../../util/urlHelpers';
import {
  WHERE_CITY,
  WHERE_MAP,
  WHERE_PLACE,
  cityNameMessageId,
  citySlugForPlaceName,
  cityWhere,
} from '../../util/unifiedSearch';

import GeocoderMapbox, { CURRENT_LOCATION_ID } from '../LocationAutocompleteInput/GeocoderMapbox';

import { useListingCounts } from './useListingCount';

import css from './WhereField.module.css';

export const KIND_CURRENT = 'current';
export const KIND_ANYWHERE = 'anywhere';
export const KIND_CITY = 'city';
export const KIND_PLACE = 'place';
export const KIND_AI = 'ai';
export const KIND_KEYWORD = 'keyword';

const PREDICTION_DEBOUNCE_MS = 200;
const MIN_PREDICTION_LENGTH = 2;

const normalise = value =>
  `${value || ''}`
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// An e-mail address or a run of 7+ digits (phone): kept away from the geocoder.
const LOOKS_PERSONAL = /@|\d[\d\s()./-]{5,}\d/;

const wordCount = value =>
  `${value || ''}`
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

export const cityUrl = slug => `/coworking/${slug}`;

/** The text a WHERE shows in the field (and in the pill). */
export const whereLabel = (where, intl) => {
  if (!where) {
    return '';
  }
  if (where.kind === WHERE_CITY) {
    return where.label || intl.formatMessage({ id: cityNameMessageId(where.citySlug) });
  }
  if (where.kind === WHERE_PLACE) {
    return where.label || `${where.address || ''}`.split(',')[0];
  }
  if (where.kind === WHERE_MAP) {
    return intl.formatMessage({ id: 'UnifiedSearch.pill.mapArea' });
  }
  return '';
};

/**
 * Our cities matching typed text: exact name or alias first (citySlugForPlaceName also knows
 * "Genève", "zuerich" …), then names starting with the text, then names containing it.
 */
const matchCities = (query, cityName) => {
  const q = normalise(query);
  if (!q) {
    return [];
  }
  const exact = citySlugForPlaceName(query);
  return CITY_LANDING_SLUGS.map((slug, order) => {
    const names = [normalise(cityName(slug)), normalise(slug)];
    const rank =
      slug === exact
        ? 0
        : names.some(n => n.startsWith(q))
        ? 1
        : names.some(n => n.includes(q))
        ? 2
        : null;
    return rank === null ? null : { slug, rank, order };
  })
    .filter(Boolean)
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .map(m => m.slug);
};

/**
 * WHERE field of the unified search — an ARIA 1.2 combobox (spec §4.1).
 *
 * Suggestions, in this order:
 * - empty field (or the applied value untouched): current location (only where the browser has
 *   geolocation — checked after mount, never in render), "Ganze Schweiz", our cities with stock
 *   (counted lazily on first focus, the number is never printed);
 * - typing: matching cities, then Mapbox places (debounced), then — for a sentence of two or more
 *   words — "… mit KI suchen" while the concierge is reachable, else "… in Angeboten suchen".
 *
 * Picking a city, place, the current location or "Ganze Schweiz" reports a WHERE through
 * `onSelectWhere`; picking the KI or keyword option hands the text to `onSearchText`, which
 * searches right away. Enter on typed text with nothing highlighted picks the first suggestion
 * (keyword search when there is none); Enter on an untouched field submits the form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.id id prefix (input `${id}-input`, listbox `${id}-listbox`)
 * @param {Object|null} props.where the draft's WHERE
 * @param {Function} props.onSelectWhere (where|null, kind|null, meta) — meta.submit when the
 *   pick came from a form submit with typed text (the editor then searches right away)
 * @param {Function} props.onSearchText (kind 'ai'|'keyword', text)
 * @param {boolean} [props.inline] listbox always visible below the input (phone dialog); else a
 *   popover that opens on focus (desktop bar)
 * @param {boolean} [props.countsEnabled] whether the city stock check may query
 * @param {boolean} [props.busy] a concierge request is running — Enter does nothing
 * @param {string} [props.placeholder]
 * @param {Function} [props.onFocus]
 * @param {string} [props.className]
 * @param {Object} ref imperative handle `{ focus(), commitPending() }`
 * @returns {JSX.Element}
 */
const WhereField = forwardRef((props, fieldRef) => {
  const {
    id,
    where,
    onSelectWhere,
    onSearchText,
    inline = false,
    countsEnabled = false,
    busy = false,
    placeholder,
    onFocus,
    className,
  } = props;
  const intl = useIntl();
  const config = useConfiguration();

  const [text, setText] = useState(() => whereLabel(where, intl));
  const [dirty, setDirty] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [predictions, setPredictions] = useState({ query: '', list: [] });
  const [hasGeolocation, setHasGeolocation] = useState(false);
  const [geoStatus, setGeoStatus] = useState('idle'); // 'idle' | 'locating' | 'error'
  const [citiesWanted, setCitiesWanted] = useState(inline);

  const rootRef = useRef(null);
  const localInputRef = useRef(null);
  const geocoderRef = useRef(null);
  const escapeConsumedRef = useRef(false);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    setHasGeolocation(typeof navigator !== 'undefined' && !!navigator.geolocation);
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // A new WHERE from outside (a pick, "Alles löschen", a new URL) resets the field to its label.
  useEffect(() => {
    setText(whereLabel(where, intl));
    setDirty(false);
    setActiveIndex(-1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [where]);

  const getGeocoder = () => {
    if (!geocoderRef.current) {
      geocoderRef.current = new GeocoderMapbox();
    }
    return geocoderRef.current;
  };

  const query = dirty ? text.trim() : '';
  const countryLimit = config?.maps?.search?.countryLimit;
  const locale = config?.localization?.locale || intl.locale;

  useEffect(() => {
    // A sentence may carry an e-mail address or a phone number: never send those to the geocoder
    // (GA4 and the keywords get redactPii; Mapbox would get the raw text on every keystroke).
    if (query.length < MIN_PREDICTION_LENGTH || LOOKS_PERSONAL.test(query)) {
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      Promise.resolve()
        .then(() => getGeocoder().getPlacePredictions(query, countryLimit, locale))
        .then(result => {
          if (!cancelled) {
            setPredictions({ query, list: result?.predictions || [] });
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPredictions({ query, list: [] });
          }
        });
    }, PREDICTION_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, countryLimit, locale]);

  const cityUrls = useMemo(() => CITY_LANDING_SLUGS.map(cityUrl), []);
  const cityCounts = useListingCounts(cityUrls, { enabled: countsEnabled && citiesWanted });
  const cityName = slug => intl.formatMessage({ id: cityNameMessageId(slug) });

  // ---- Suggestions ---- //
  const sections = [];
  if (!query) {
    const top = [];
    if (hasGeolocation && config?.maps?.search?.suggestCurrentLocation !== false) {
      top.push({ kind: KIND_CURRENT, key: 'current' });
    }
    top.push({ kind: KIND_ANYWHERE, key: 'anywhere' });
    sections.push({ key: 'top', options: top });
    // Only cities known to have spaces: listing all nine while the counts load let a fast click
    // pick one without any (Lausanne, 0 — seen in the 1280px run 2026-10-18), and the list then
    // shrank under the pointer. Without counts (not enabled) every city stays.
    const countsWanted = countsEnabled && citiesWanted;
    const cities = CITY_LANDING_SLUGS.filter(slug =>
      countsWanted ? cityCounts[cityUrl(slug)] > 0 : true
    ).map(slug => ({
      kind: KIND_CITY,
      key: `city-${slug}`,
      slug,
    }));
    if (cities.length > 0) {
      sections.push({
        key: 'cities',
        heading: intl.formatMessage({ id: 'UnifiedSearch.where.citiesHeading' }),
        options: cities,
      });
    }
  } else {
    const citySlugs = matchCities(query, cityName);
    if (citySlugs.length > 0) {
      sections.push({
        key: 'cities',
        heading: intl.formatMessage({ id: 'UnifiedSearch.where.citiesHeading' }),
        options: citySlugs.map(slug => ({ kind: KIND_CITY, key: `city-${slug}`, slug })),
      });
    }
    // A Mapbox "Zürich, Zürich, Schweiz" is our city Zürich — listed once, as the city.
    // Only the answer for THIS text: the list of a previous query stayed pickable until the next
    // debounced response, so a quick Enter after retyping sent the visitor to the old place.
    const places = (predictions.query === query ? predictions.list : [])
      .filter(p => {
        const slug = citySlugForPlaceName(p?.place_name);
        return !(slug && citySlugs.includes(slug));
      })
      .map((prediction, i) => ({
        kind: KIND_PLACE,
        key: `place-${prediction?.id || i}`,
        prediction,
      }));
    if (places.length > 0) {
      sections.push({
        key: 'places',
        heading: intl.formatMessage({ id: 'UnifiedSearch.where.placesHeading' }),
        options: places,
      });
    }
    if (wordCount(query) >= 2) {
      const aiAvailable = isAiConciergeEnabled() && !isAiConciergeUnavailable();
      const textSection = {
        key: 'text',
        options: [{ kind: aiAvailable ? KIND_AI : KIND_KEYWORD, key: 'text' }],
      };
      // A sentence ("Meetingraum für 10 in Zürich") is the KI's: it goes first, so Enter and the
      // submit button take it instead of the Mapbox "Zürich" the fuzzy geocoder also returns —
      // that dropped the type and the people count. Two words without a city match ("Zürich
      // Oerlikon") stay a place unless the concierge can read them.
      const isSentence =
        wordCount(query) >= 3 || (aiAvailable && citySlugs.length === 0 && places.length === 0);
      if (isSentence) {
        sections.unshift(textSection);
      } else {
        sections.push(textSection);
      }
    }
  }
  const options = sections.flatMap(s => s.options);
  const listVisible = inline || isOpen;
  const isExpanded = listVisible && options.length > 0;
  const active = listVisible && activeIndex < options.length ? activeIndex : -1;
  const inputId = `${id}-input`;
  const listboxId = `${id}-listbox`;
  const statusId = `${id}-status`;
  const optionId = index => `${id}-option-${index}`;

  useEffect(() => {
    if (active >= 0) {
      const el = document.getElementById(optionId(active));
      el?.scrollIntoView?.({ block: 'nearest' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // ---- Choosing ---- //
  const selectCurrentLocation = meta => {
    setGeoStatus('locating');
    const distance = config?.maps?.search?.currentLocationBoundsDistance;
    Promise.resolve()
      .then(() =>
        getGeocoder().getPlaceDetails({ id: CURRENT_LOCATION_ID, predictionPlace: {} }, distance)
      )
      .then(place => {
        if (!isMountedRef.current) {
          return;
        }
        if (!place?.bounds) {
          setGeoStatus('error');
          return;
        }
        setGeoStatus('idle');
        const label = intl.formatMessage({ id: 'UnifiedSearch.where.currentLocation' });
        onSelectWhere(
          {
            kind: WHERE_PLACE,
            address: label,
            boundsStr: encodeLatLngBounds(place.bounds),
            label,
            citySlug: null,
          },
          KIND_CURRENT,
          meta
        );
      })
      .catch(() => {
        if (isMountedRef.current) {
          setGeoStatus('error');
        }
      });
  };

  const selectPlace = (prediction, meta) => {
    Promise.resolve()
      .then(() => getGeocoder().getPlaceDetails(prediction))
      .then(place => {
        if (!isMountedRef.current || !place?.bounds) {
          return;
        }
        const address = place.address || prediction?.place_name || '';
        onSelectWhere(
          {
            kind: WHERE_PLACE,
            address,
            boundsStr: encodeLatLngBounds(place.bounds),
            label: address.split(',')[0],
            citySlug: citySlugForPlaceName(address),
          },
          KIND_PLACE,
          meta
        );
      })
      .catch(() => {
        // A place without details cannot be searched by bounds; the text stays in the field.
      });
  };

  const choose = (option, meta = {}) => {
    if (!option || busy) {
      return;
    }
    setActiveIndex(-1);
    if (!inline) {
      setIsOpen(false);
    }
    if (option.kind === KIND_CURRENT) {
      selectCurrentLocation(meta);
    } else if (option.kind === KIND_ANYWHERE) {
      setGeoStatus('idle');
      onSelectWhere(null, KIND_ANYWHERE, meta);
    } else if (option.kind === KIND_CITY) {
      setGeoStatus('idle');
      onSelectWhere(cityWhere(option.slug, cityName(option.slug)), KIND_CITY, meta);
    } else if (option.kind === KIND_PLACE) {
      setGeoStatus('idle');
      selectPlace(option.prediction, meta);
    } else if (option.kind === KIND_AI || option.kind === KIND_KEYWORD) {
      onSearchText(option.kind, query);
    }
  };

  /**
   * Typed text nobody picked a suggestion for, when the form is submitted: pick the first
   * suggestion (keyword search when there is none) and let the editor search with it.
   *
   * @returns {boolean} true when the field took over the submit
   */
  const commitPending = () => {
    if (!query) {
      return false;
    }
    if (!busy) {
      if (options.length > 0) {
        choose(options[0], { submit: true });
      } else {
        onSearchText(KIND_KEYWORD, query);
      }
    }
    return true;
  };

  useImperativeHandle(fieldRef, () => ({
    focus: () => localInputRef.current?.focus(),
    commitPending,
  }));

  // ---- Events ---- //
  const handleFocus = event => {
    setCitiesWanted(true);
    if (!inline) {
      setIsOpen(true);
    }
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur = event => {
    if (inline) {
      return;
    }
    const next = event.relatedTarget;
    if (!next || !rootRef.current?.contains(next)) {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  const handleChange = event => {
    setText(event.target.value);
    setDirty(true);
    setActiveIndex(-1);
    if (!inline) {
      setIsOpen(true);
    }
  };

  const consumeEscape = event => {
    event.preventDefault();
    event.stopPropagation();
    escapeConsumedRef.current = true;
  };

  const handleKeyDown = event => {
    const count = options.length;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!listVisible) {
        setIsOpen(true);
      }
      if (count > 0) {
        setActiveIndex(active < 0 ? 0 : Math.min(count - 1, active + 1));
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (count > 0) {
        setActiveIndex(active < 0 ? count - 1 : Math.max(0, active - 1));
      }
    } else if (event.key === 'Enter') {
      if (busy) {
        event.preventDefault();
      } else if (active >= 0) {
        event.preventDefault();
        choose(options[active]);
      } else if (query) {
        event.preventDefault();
        if (count > 0) {
          choose(options[0]);
        } else {
          onSearchText(KIND_KEYWORD, query);
        }
      }
      // Untouched field and nothing highlighted: the form submits the search.
    } else if (event.key === 'Escape') {
      if (active >= 0) {
        consumeEscape(event);
        setActiveIndex(-1);
      } else if (!inline && isOpen) {
        consumeEscape(event);
        setIsOpen(false);
      }
    }
  };

  // The phone dialog (Modal) closes on Escape *keyup* on <body>; an Escape that only closed the
  // suggestion highlight must not travel on and close the whole dialog.
  const handleKeyUp = event => {
    if (event.key === 'Escape' && escapeConsumedRef.current) {
      escapeConsumedRef.current = false;
      event.stopPropagation();
    }
  };

  const handleClear = () => {
    setGeoStatus('idle');
    setText('');
    setDirty(false);
    setActiveIndex(-1);
    onSelectWhere(null, null);
    localInputRef.current?.focus();
  };

  // ---- Render ---- //
  const optionContent = option => {
    switch (option.kind) {
      case KIND_CURRENT:
        return {
          Icon: Navigation,
          label: intl.formatMessage({ id: 'UnifiedSearch.where.currentLocation' }),
          sub:
            geoStatus === 'locating'
              ? intl.formatMessage({ id: 'UnifiedSearch.where.locating' })
              : null,
        };
      case KIND_ANYWHERE:
        return {
          Icon: Globe,
          label: intl.formatMessage({ id: 'UnifiedSearch.where.anywhere' }),
        };
      case KIND_CITY:
        return { Icon: MapPin, label: cityName(option.slug) };
      case KIND_PLACE: {
        const name = `${option.prediction?.place_name || ''}`;
        const [first, ...rest] = name.split(',');
        return { Icon: MapPin, label: first.trim(), sub: rest.join(',').trim() || null };
      }
      case KIND_AI:
        return {
          Icon: Sparkles,
          label: intl.formatMessage({ id: 'UnifiedSearch.where.askAi' }, { text: query }),
        };
      default:
        return {
          Icon: Search,
          label: intl.formatMessage({ id: 'UnifiedSearch.where.keyword' }, { text: query }),
        };
    }
  };

  let flatIndex = -1;
  const renderOption = option => {
    flatIndex += 1;
    const index = flatIndex;
    const { Icon, label, sub } = optionContent(option);
    return (
      <div
        key={option.key}
        id={optionId(index)}
        role="option"
        aria-selected={index === active}
        className={classNames(css.option, { [css.optionActive]: index === active })}
        data-testid="unified-search-where-option"
        data-kind={option.kind}
        onMouseDown={event => event.preventDefault()}
        onClick={() => choose(option)}
      >
        <span className={css.optionIcon} aria-hidden="true">
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <span className={css.optionText}>
          <span className={css.optionLabel}>{label}</span>
          {sub ? <span className={css.optionSub}>{sub}</span> : null}
        </span>
      </div>
    );
  };

  const statusText =
    geoStatus === 'error'
      ? intl.formatMessage({ id: 'UnifiedSearch.where.locationError' })
      : geoStatus === 'locating'
      ? intl.formatMessage({ id: 'UnifiedSearch.where.locating' })
      : '';

  return (
    <div className={classNames(css.root, className)} ref={rootRef} onBlur={handleBlur}>
      <div className={css.inputRow}>
        <input
          ref={localInputRef}
          id={inputId}
          className={css.input}
          type="text"
          role="combobox"
          aria-label={intl.formatMessage({ id: 'UnifiedSearch.where.label' })}
          aria-expanded={isExpanded}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? optionId(active) : undefined}
          aria-describedby={statusText ? statusId : undefined}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          placeholder={placeholder || intl.formatMessage({ id: 'UnifiedSearch.where.placeholder' })}
          value={text}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          data-testid="unified-search-where-input"
        />
        {text ? (
          <button
            type="button"
            className={css.clear}
            aria-label={intl.formatMessage({ id: 'UnifiedSearch.where.clear' })}
            onClick={handleClear}
          >
            <X size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <div
        id={listboxId}
        role="listbox"
        aria-label={intl.formatMessage({ id: 'UnifiedSearch.where.listboxLabel' })}
        className={inline ? css.listInline : css.listPopover}
        hidden={!isExpanded}
      >
        {listVisible
          ? sections.map(section =>
              section.heading ? (
                <div
                  key={section.key}
                  role="group"
                  aria-labelledby={`${id}-heading-${section.key}`}
                  className={css.group}
                >
                  <div
                    id={`${id}-heading-${section.key}`}
                    role="presentation"
                    className={css.heading}
                  >
                    {section.heading}
                  </div>
                  {section.options.map(renderOption)}
                </div>
              ) : (
                <div key={section.key} role="group" className={css.group}>
                  {section.options.map(renderOption)}
                </div>
              )
            )
          : null}
      </div>
      <p id={statusId} className={css.status} role="status" aria-live="polite">
        {statusText}
      </p>
    </div>
  );
});

WhereField.displayName = 'WhereField';

export default WhereField;
